/* eslint-disable jsx-a11y/anchor-is-valid */
import { BreakpointQueryName } from '@doveit/lego';
import React, {
  FunctionComponent, useCallback, useMemo,
} from 'react';
import { getStructuredData, getBreadcrumbStructuredData } from '../../../../data/structured';
import { Adv } from '../../../../domain/adv';
import { ArticleType } from '../../../../domain/articleType';
import { Guide } from '../../../../domain/graphcms';
import ArticlesList from '../../../components/articles-list/ArticlesList';
import PageHead from '../../../components/page-head/PageHead';
import { mapArticles } from '../../../graphql/mappers/articleMappers';
import AppLayout from '../../../layouts/app-layout/AppLayout';
import {
  goToPage, QueryObject, buildArticlesURLObject, slugIt,
} from '../../../utils/links';
import { ArticlesBreadcrumb } from '../../../constants';
import * as styles from './GuidesPage.style';
import RelatedArticles, { RelatedArticleItem } from '../../../components/related-articles/RelatedArticles';
import AdvFluid from '../../../components/adv-fluid/AdvFluid';
import PageLayout from '../../../layouts/page-layout/PageLayout';
import BottomBarV2 from '../bottom-bar-v2/BottomBarV2';
import useRefInView from '../../../hooks/useRefInView/useRefInView';
import CategoryList from '../../../components/category-list/CategoryList';
import { CategoryItem } from '../../../types';
import { BottomBarData } from '../../../../data/bottom-bars';

export const PAGE_SIZE = 30;

export interface GuidesPageProps {
  query: QueryObject,
  title: string,
  category?: string,
  categories?: CategoryItem[],
  articleType: ArticleType,
  SEO: {
    title: string,
    description: string,
  },
  guides: Guide[],
  page: number,
  totalPages: number,
  totalItems: number,
  advSide: Adv,
  advSerp: Adv,
  promotionalLinks: Omit<RelatedArticleItem, 'onClick'>[],
  bottomBar: BottomBarData,
}

const INTERSECTION_OPTIONS = { threshold: 1 };

const GuidesPage: FunctionComponent<GuidesPageProps> = ({
  query,
  title,
  articleType,
  SEO,
  guides,
  categories = [],
  category,
  page,
  totalPages,
  totalItems,
  advSide,
  advSerp,
  promotionalLinks,
  bottomBar,
}) => {
  const { ref: headerRef, entry: header } = useRefInView(INTERSECTION_OPTIONS);
  const { ref: relatedArticlesRef, entry: relatedArticles } = useRefInView(INTERSECTION_OPTIONS);

  const advInsertion = (index) => (
    index === 2 && (
      <AdvFluid
        {...advSerp}
        dataRef="adv-fluid-v1-main-sell-properties"
        dataTracking="adv-fluid-v1-main-sell-properties"
      />
    )
  );

  const getPageUrl = useCallback((requestedPage) => buildArticlesURLObject({ ...query, p: requestedPage }, articleType).as, [articleType, query]);
  const onPageChange = useCallback((requestedPage) => goToPage(buildArticlesURLObject({ ...query, p: requestedPage }, articleType)), [articleType, query]);

  const breadcrumb = useMemo(() => {
    const items = [{
      label: ArticlesBreadcrumb[articleType],
      urlObject: buildArticlesURLObject({
        ...query,
        category: undefined,
      }, articleType),
    }];

    if (category) {
      items.push({
        label: category,
        urlObject: buildArticlesURLObject(query, articleType),
      });
    }

    return items;
  }, [articleType, category, query]);

  const goToBottomBarLink = useCallback(
    () => goToPage(bottomBar.urlObject),
    [bottomBar],
  );

  return (
    <AppLayout>
      <PageHead
        SEO={SEO}
        structuredData={getStructuredData(getBreadcrumbStructuredData(breadcrumb))}
      />
      <PageLayout>
        <PageLayout.Wrapper>
          <PageLayout.Header
            data-ref="blog-layout-header"
          >
            <div
              ref={headerRef}
              css={styles.header}
            >
              <h1 css={styles.title}>
                {title}
              </h1>
              {categories?.length > 0 && (
                <nav css={styles.categories}>
                  <CategoryList
                    items={categories}
                    activeId={category ? slugIt(category) : 'tutte-le-guide'}
                  />
                </nav>
              )}
            </div>
          </PageLayout.Header>
          <PageLayout.Content>
            <PageLayout.Main>
              <ArticlesList
                articles={mapArticles(guides, articleType)}
                render={{ inPosition: advInsertion }}
                pagination={{
                  totalPages,
                  totalElements: totalItems,
                  pageNumber: page - 1,
                  pageSize: PAGE_SIZE,
                  onPageChange,
                  buildPagePath: getPageUrl,
                }}
              />
            </PageLayout.Main>
            <PageLayout.Aside>
              <AdvFluid
                {...advSide}
                dataRef="adv-fluid-v1-aside-sell-properties"
                dataTracking="adv-fluid-v1-aside-sell-properties"
              />
              <div
                ref={relatedArticlesRef}
                css={styles.relatedArticles}
              >
                <RelatedArticles
                  title="Vedi anche"
                  items={promotionalLinks}
                />
              </div>
              <div
                $visible={relatedArticles?.boundingClientRect.y < 0}
                css={styles.stickySideAdv}
                data-ref="adv-fluid-sticky-aside"
              >
                <AdvFluid
                  {...advSide}
                  dataRef="adv-fluid-v1-sticky-sell-properties"
                  dataTracking="adv-fluid-v1-sticky-sell-properties"
                />
              </div>
            </PageLayout.Aside>
          </PageLayout.Content>
          <div css={styles.footer}>
            <PageLayout.Breadcrumb items={breadcrumb} />
          </div>
        </PageLayout.Wrapper>
        <BottomBarV2
          hiddenFrom={BreakpointQueryName.LG}
          visible={header?.boundingClientRect.y < 0}
          label={bottomBar.label}
          title={bottomBar.title}
          href={bottomBar.urlObject?.as}
          onClick={goToBottomBarLink}
        />
      </PageLayout>
    </AppLayout>
  );
};

export default GuidesPage;
